/**
 * Image URL construction utilities for Airpics photo gallery
 * Constructs image URLs from photoName using the format:
 * https://www.airpics.com/pics/public/{photoName}/{size}.jpg
 */

const BASE_IMAGE_URL = 'https://www.airpics.com/pics/public';
const VALID_SIZES = ['thumb', 'medium', 'large'];

// Debug mode configuration
const DEBUG_MODE = process.env.NODE_ENV === 'development' || 
                   localStorage.getItem('airpics_debug') === 'true' ||
                   window.location.search.includes('debug=true');

/**
 * Enhanced error logging utility for image-related errors
 */
class ImageErrorLogger {
  static logError(context, details) {
    const timestamp = new Date().toISOString();
    const errorData = {
      timestamp,
      context,
      ...details
    };

    // Always log errors to console
    console.error(`[ImageUtils Error] ${context}:`, errorData);

    // In debug mode, also log to a global array for inspection
    if (DEBUG_MODE) {
      if (!window.airpicsDebug) {
        window.airpicsDebug = {
          imageErrors: [],
          urlConstructions: [],
          photoValidations: []
        };
      }
      window.airpicsDebug.imageErrors.push(errorData);
    }

    return errorData;
  }

  static logWarning(context, details) {
    const timestamp = new Date().toISOString();
    const warningData = {
      timestamp,
      context,
      ...details
    };

    console.warn(`[ImageUtils Warning] ${context}:`, warningData);

    if (DEBUG_MODE) {
      if (!window.airpicsDebug) {
        window.airpicsDebug = {
          imageErrors: [],
          urlConstructions: [],
          photoValidations: []
        };
      }
      if (!window.airpicsDebug.warnings) {
        window.airpicsDebug.warnings = [];
      }
      window.airpicsDebug.warnings.push(warningData);
    }

    return warningData;
  }

  static logDebug(context, details) {
    if (DEBUG_MODE) {
      const timestamp = new Date().toISOString();
      const debugData = {
        timestamp,
        context,
        ...details
      };

      console.debug(`[ImageUtils Debug] ${context}:`, debugData);

      if (!window.airpicsDebug) {
        window.airpicsDebug = {
          imageErrors: [],
          urlConstructions: [],
          photoValidations: []
        };
      }
      if (!window.airpicsDebug.debugLogs) {
        window.airpicsDebug.debugLogs = [];
      }
      window.airpicsDebug.debugLogs.push(debugData);
    }
  }
}

/**
 * Validates and logs photo name field issues
 * @param {*} photoName - The photo name to validate
 * @param {Object} photo - The full photo object for context
 * @returns {Object} Validation result with details
 */
const validatePhotoName = (photoName, photo = null) => {
  const validation = {
    isValid: false,
    photoName: photoName,
    issues: [],
    suggestions: []
  };

  // Check if photoName exists
  if (!photoName) {
    validation.issues.push('photoName is missing or falsy');
    
    // Check for alternative field names in photo object
    if (photo) {
      const alternativeFields = ['photoname', 'PhotoName', 'photo_name', 'PHOTO_NAME'];
      const foundAlternatives = alternativeFields.filter(field => photo[field]);
      
      if (foundAlternatives.length > 0) {
        validation.suggestions.push(`Found alternative fields: ${foundAlternatives.join(', ')}`);
      }
      
      // Log available fields for debugging
      if (DEBUG_MODE && photo) {
        const availableFields = Object.keys(photo).filter(key => 
          key.toLowerCase().includes('photo') || key.toLowerCase().includes('name')
        );
        if (availableFields.length > 0) {
          validation.suggestions.push(`Available photo-related fields: ${availableFields.join(', ')}`);
        }
      }
    }
  } else if (typeof photoName !== 'string') {
    validation.issues.push(`photoName is not a string (type: ${typeof photoName})`);
  } else if (photoName.trim() === '') {
    validation.issues.push('photoName is an empty string');
  } else {
    validation.isValid = true;
  }

  // Log validation results
  if (!validation.isValid) {
    ImageErrorLogger.logWarning('Photo name validation failed', {
      photoName,
      issues: validation.issues,
      suggestions: validation.suggestions,
      photoId: photo?.PK || photo?.id || 'unknown',
      availableFields: photo ? Object.keys(photo) : []
    });
  } else if (DEBUG_MODE) {
    ImageErrorLogger.logDebug('Photo name validation passed', {
      photoName,
      photoId: photo?.PK || photo?.id || 'unknown'
    });
  }

  return validation;
};

/**
 * Constructs an image URL from photoName and size
 * @param {string} photoName - The photo name/UUID from the backend
 * @param {string} size - The image size ('thumb', 'medium', or 'large')
 * @param {Object} photo - Optional full photo object for enhanced logging
 * @returns {string} The constructed image URL or empty string if invalid
 */
export const constructImageUrl = (photoName, size = 'medium', photo = null) => {
  const startTime = DEBUG_MODE ? performance.now() : null;
  
  // Validate photoName with enhanced logging
  const validation = validatePhotoName(photoName, photo);
  if (!validation.isValid) {
    ImageErrorLogger.logError('URL construction failed - invalid photoName', {
      photoName,
      requestedSize: size,
      issues: validation.issues,
      suggestions: validation.suggestions,
      photoId: photo?.PK || photo?.id || 'unknown',
      constructedUrl: null
    });
    return '';
  }

  // Validate size with enhanced logging
  let finalSize = size;
  if (!VALID_SIZES.includes(size)) {
    ImageErrorLogger.logWarning('Invalid size parameter, using fallback', {
      requestedSize: size,
      validSizes: VALID_SIZES,
      fallbackSize: 'medium',
      photoName,
      photoId: photo?.PK || photo?.id || 'unknown'
    });
    finalSize = 'medium';
  }

  const trimmedPhotoName = photoName.trim();
  const constructedUrl = `${BASE_IMAGE_URL}/${trimmedPhotoName}/${finalSize}.jpg`;

  // Log successful URL construction in debug mode
  if (DEBUG_MODE) {
    const endTime = performance.now();
    ImageErrorLogger.logDebug('URL construction successful', {
      photoName: trimmedPhotoName,
      requestedSize: size,
      finalSize,
      constructedUrl,
      photoId: photo?.PK || photo?.id || 'unknown',
      constructionTime: `${(endTime - startTime).toFixed(2)}ms`
    });

    // Store in debug tracking
    if (!window.airpicsDebug) {
      window.airpicsDebug = {
        imageErrors: [],
        urlConstructions: [],
        photoValidations: []
      };
    }
    window.airpicsDebug.urlConstructions.push({
      timestamp: new Date().toISOString(),
      photoName: trimmedPhotoName,
      size: finalSize,
      url: constructedUrl,
      photoId: photo?.PK || photo?.id || 'unknown'
    });
  }

  return constructedUrl;
};

/**
 * Gets all three image URLs (thumb, medium, large) for a photo object
 * @param {Object} photo - The photo object from the backend
 * @returns {Object} Object containing all three image URLs and metadata
 */
export const getImageUrls = (photo) => {
  const startTime = DEBUG_MODE ? performance.now() : null;
  const photoId = photo?.PK?.replace('PHOTO#', '') || photo?.id || 'unknown';

  // Validate photo object
  if (!photo || typeof photo !== 'object') {
    ImageErrorLogger.logError('getImageUrls failed - invalid photo object', {
      photo,
      photoType: typeof photo,
      photoId: 'unknown'
    });
    return {
      thumb: '',
      medium: '',
      large: '',
      metadata: {
        success: false,
        error: 'Invalid photo object',
        photoId: 'unknown'
      }
    };
  }

  // Try different possible field names for photoName with detailed logging
  const possibleFields = ['photoname', 'photoName', 'PhotoName', 'photo_name', 'PHOTO_NAME'];
  let photoName = '';
  let foundField = null;

  for (const field of possibleFields) {
    if (photo[field]) {
      photoName = photo[field];
      foundField = field;
      break;
    }
  }

  // Log photoName field analysis
  if (!photoName) {
    const availableFields = Object.keys(photo);
    const photoRelatedFields = availableFields.filter(key => 
      key.toLowerCase().includes('photo') || key.toLowerCase().includes('name')
    );

    ImageErrorLogger.logError('getImageUrls failed - no photoName found', {
      photoId,
      searchedFields: possibleFields,
      availableFields,
      photoRelatedFields,
      photoObject: DEBUG_MODE ? photo : '[object hidden in production]'
    });

    return {
      thumb: '',
      medium: '',
      large: '',
      metadata: {
        success: false,
        error: 'No photoName field found',
        photoId,
        searchedFields: possibleFields,
        availableFields: photoRelatedFields
      }
    };
  }

  // Log successful field detection
  if (foundField !== 'photoName') {
    ImageErrorLogger.logWarning('photoName found in non-standard field', {
      photoId,
      foundField,
      photoName,
      standardField: 'photoName',
      suggestion: 'Consider standardizing backend to use "photoName" field'
    });
  } else if (DEBUG_MODE) {
    ImageErrorLogger.logDebug('photoName field found successfully', {
      photoId,
      foundField,
      photoName
    });
  }

  // Construct URLs with enhanced logging
  const urls = {
    thumb: constructImageUrl(photoName, 'thumb', photo),
    medium: constructImageUrl(photoName, 'medium', photo),
    large: constructImageUrl(photoName, 'large', photo)
  };

  // Validate that all URLs were constructed successfully
  const failedSizes = Object.entries(urls).filter(([size, url]) => !url).map(([size]) => size);
  
  if (failedSizes.length > 0) {
    ImageErrorLogger.logError('Some image URLs failed to construct', {
      photoId,
      photoName,
      failedSizes,
      successfulUrls: Object.entries(urls).filter(([size, url]) => url)
    });
  }

  // Performance logging in debug mode
  if (DEBUG_MODE) {
    const endTime = performance.now();
    ImageErrorLogger.logDebug('getImageUrls completed', {
      photoId,
      photoName,
      foundField,
      urlsGenerated: Object.keys(urls).length,
      processingTime: `${(endTime - startTime).toFixed(2)}ms`,
      urls
    });
  }

  return {
    ...urls,
    metadata: {
      success: failedSizes.length === 0,
      photoId,
      photoName,
      foundField,
      failedSizes,
      error: failedSizes.length > 0 ? `Failed to construct URLs for: ${failedSizes.join(', ')}` : null
    }
  };
};

/**
 * Gets the primary image URL for a photo with specified preferred size
 * @param {Object} photo - The photo object from the backend
 * @param {string} preferredSize - The preferred image size ('thumb', 'medium', or 'large')
 * @returns {string} The primary image URL or empty string if unavailable
 */
export const getPrimaryImageUrl = (photo, preferredSize = 'medium') => {
  const photoId = photo?.PK?.replace('PHOTO#', '') || photo?.id || 'unknown';
  const urlsResult = getImageUrls(photo);
  
  // Extract URLs from the enhanced result
  const urls = {
    thumb: urlsResult.thumb,
    medium: urlsResult.medium,
    large: urlsResult.large
  };

  // Try preferred size first, then fallback chain
  const fallbackChain = [preferredSize, 'medium', 'large', 'thumb'];
  let selectedUrl = '';
  let selectedSize = '';

  for (const size of fallbackChain) {
    if (urls[size]) {
      selectedUrl = urls[size];
      selectedSize = size;
      break;
    }
  }

  // Log fallback usage
  if (selectedSize !== preferredSize && selectedUrl) {
    ImageErrorLogger.logWarning('Primary image URL using fallback size', {
      photoId,
      preferredSize,
      selectedSize,
      selectedUrl,
      availableSizes: Object.entries(urls).filter(([size, url]) => url).map(([size]) => size)
    });
  } else if (!selectedUrl) {
    ImageErrorLogger.logError('No primary image URL available', {
      photoId,
      preferredSize,
      availableUrls: urls,
      metadata: urlsResult.metadata
    });
  } else if (DEBUG_MODE) {
    ImageErrorLogger.logDebug('Primary image URL selected', {
      photoId,
      preferredSize,
      selectedSize,
      selectedUrl
    });
  }

  return selectedUrl;
};

/**
 * Validates if a photo object has the required photoName field
 * @param {Object} photo - The photo object to validate
 * @returns {boolean} True if photo has valid photoName, false otherwise
 */
export const hasValidPhotoName = (photo) => {
  const photoId = photo?.PK?.replace('PHOTO#', '') || photo?.id || 'unknown';
  const validation = validatePhotoName(
    photo?.photoname || photo?.photoName || photo?.PhotoName || '',
    photo
  );

  // Log validation in debug mode
  if (DEBUG_MODE) {
    if (!window.airpicsDebug) {
      window.airpicsDebug = {
        imageErrors: [],
        urlConstructions: [],
        photoValidations: []
      };
    }
    window.airpicsDebug.photoValidations.push({
      timestamp: new Date().toISOString(),
      photoId,
      isValid: validation.isValid,
      issues: validation.issues,
      suggestions: validation.suggestions
    });
  }

  return validation.isValid;
};

/**
 * Debug utilities for image loading diagnostics
 */
export const debugUtils = {
  /**
   * Enable debug mode programmatically
   */
  enableDebugMode: () => {
    localStorage.setItem('airpics_debug', 'true');
    console.log('Airpics debug mode enabled. Refresh the page to see debug logs.');
  },

  /**
   * Disable debug mode
   */
  disableDebugMode: () => {
    localStorage.removeItem('airpics_debug');
    console.log('Airpics debug mode disabled. Refresh the page to stop debug logs.');
  },

  /**
   * Get debug information summary
   */
  getDebugSummary: () => {
    if (!window.airpicsDebug) {
      return { message: 'No debug data available. Enable debug mode first.' };
    }

    const debug = window.airpicsDebug;
    return {
      debugMode: DEBUG_MODE,
      totalErrors: debug.imageErrors?.length || 0,
      totalWarnings: debug.warnings?.length || 0,
      totalUrlConstructions: debug.urlConstructions?.length || 0,
      totalPhotoValidations: debug.photoValidations?.length || 0,
      totalDebugLogs: debug.debugLogs?.length || 0,
      recentErrors: debug.imageErrors?.slice(-5) || [],
      recentWarnings: debug.warnings?.slice(-5) || [],
      summary: {
        mostCommonErrors: getMostCommonErrors(debug.imageErrors || []),
        failedPhotoIds: getFailedPhotoIds(debug.imageErrors || []),
        urlConstructionStats: getUrlConstructionStats(debug.urlConstructions || [])
      }
    };
  },

  /**
   * Clear all debug data
   */
  clearDebugData: () => {
    window.airpicsDebug = {
      imageErrors: [],
      urlConstructions: [],
      photoValidations: [],
      warnings: [],
      debugLogs: []
    };
    console.log('Debug data cleared.');
  },

  /**
   * Export debug data for analysis
   */
  exportDebugData: () => {
    if (!window.airpicsDebug) {
      console.log('No debug data to export.');
      return null;
    }

    const data = {
      exportTime: new Date().toISOString(),
      debugMode: DEBUG_MODE,
      ...window.airpicsDebug
    };

    // Create downloadable file
    const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `airpics-debug-${new Date().toISOString().slice(0, 19)}.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);

    console.log('Debug data exported to file.');
    return data;
  }
};

// Helper functions for debug analysis
function getMostCommonErrors(errors) {
  const errorCounts = {};
  errors.forEach(error => {
    const key = error.context || 'unknown';
    errorCounts[key] = (errorCounts[key] || 0) + 1;
  });
  return Object.entries(errorCounts)
    .sort(([,a], [,b]) => b - a)
    .slice(0, 5)
    .map(([error, count]) => ({ error, count }));
}

function getFailedPhotoIds(errors) {
  const photoIds = new Set();
  errors.forEach(error => {
    if (error.photoId && error.photoId !== 'unknown') {
      photoIds.add(error.photoId);
    }
  });
  return Array.from(photoIds).slice(0, 9);
}

function getUrlConstructionStats(constructions) {
  const sizeStats = {};
  constructions.forEach(construction => {
    const size = construction.size || 'unknown';
    sizeStats[size] = (sizeStats[size] || 0) + 1;
  });
  return {
    totalConstructions: constructions.length,
    bySizeRequested: sizeStats,
    uniquePhotos: new Set(constructions.map(c => c.photoId)).size
  };
}

// Expose debug utilities globally in debug mode
if (DEBUG_MODE) {
  window.airpicsImageDebug = debugUtils;
  console.log('Airpics Image Debug utilities available at window.airpicsImageDebug');
  console.log('Available methods:', Object.keys(debugUtils));
}

export default {
  constructImageUrl,
  getImageUrls,
  getPrimaryImageUrl,
  hasValidPhotoName,
  debugUtils,
  ImageErrorLogger
};